import React, { useState, useEffect, useRef, ReactNode } from 'react';
import {
  authService,
  clearProtectedDataCaches,
  forceClientAuthLogout,
  scheduleClientAuthExpiry,
} from '@/services/api';
import type {
  FrontendAccessPolicy,
  LoginCredentials,
  PlatformEntryContext,
  RegistrationPayload,
  User,
} from '@/types';
import { AuthContext } from './AuthContextInstance';
import {
  buildFailClosedFrontendAccessPolicy,
  buildFallbackFrontendAccessPolicy,
  buildFallbackPlatformEntryContext,
  resolveTransientPlatformEntryContext,
} from '@/utils/access';
import { clearActiveContour } from '@/utils/activeContour';
import { clearActiveWorkspace } from '@/utils/activeWorkspace';
import {
  buildAuthBootstrapSnapshot,
  buildAccessEnvelopeFingerprint,
  buildJwtBootstrapFallbackUser,
  getAuthBootstrapStorageKey,
  readUsableAuthBootstrapSnapshot,
} from '@/utils/authBootstrap';
import { getUserFacingErrorStatus } from '@/utils/userFacingError';
import { AuthRequestGuard, type AuthRequestLease } from '@/utils/authRequestGuard';
import { readWorkspaceHandoffUrl } from '@/utils/loginPageMode';
import { clearWorkspaceResume } from '@/utils/workspaceResume';

const AUTH_CHECK_TIMEOUT_MS = 8000;

const isLocalhost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.'));

// При туннель-прокси всегда используем реальный JWT с бэка, без фейкового dev-режима (не React Hook — просто проверка env).
const isTunnelProxyEnabled = () =>
  import.meta.env.VITE_USE_TUNNEL_PROXY === 'true' || import.meta.env.VITE_USE_TUNNEL_PROXY === '1';

// Bypass = фейковый пользователь без реального логина. На localhost он включён
// по умолчанию, чтобы локальная приёмка не зависела от рабочего backend-login.
const getDevBypassAuth = () => {
  if (isTunnelProxyEnabled()) return false;
  return import.meta.env.VITE_DEV_BYPASS_AUTH === 'true' || isLocalhost();
};

const getDevBypassAutoLogin = () =>
  getDevBypassAuth() && isLocalhost();

const buildDevBypassUser = (username = 'dev'): User => ({
  id: 1,
  username,
  first_name: username,
  last_name: 'Test',
  email: `${username}@test.local`,
  tenant_id: 'artem',
  role: 'developer',
  enabled: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

const buildDevBypassEntryContext = (
  user: User,
  accessPolicy: FrontendAccessPolicy,
): PlatformEntryContext => buildFallbackPlatformEntryContext(
  user,
  accessPolicy,
  `${user.tenant_id || 'artem'}.moy-gorod.su`,
);

const readStoredAuthBootstrap = (token: string | null): ReturnType<typeof readUsableAuthBootstrapSnapshot> => {
  if (typeof window === 'undefined') return null;
  try {
    const key = getAuthBootstrapStorageKey(window.location.host);
    return readUsableAuthBootstrapSnapshot(window.localStorage.getItem(key), token);
  } catch {
    return null;
  }
};

const writeStoredAuthBootstrap = (
  user: User,
  accessPolicy: FrontendAccessPolicy | null,
  entryContext: PlatformEntryContext | null,
): void => {
  if (typeof window === 'undefined') return;
  try {
    const key = getAuthBootstrapStorageKey(window.location.host);
    window.localStorage.setItem(
      key,
      JSON.stringify(buildAuthBootstrapSnapshot(user, accessPolicy, Date.now(), entryContext)),
    );
  } catch {
    // ignore
  }
};

const clearStoredAuthBootstrap = (): void => {
  if (typeof window === 'undefined') return;
  try {
    const key = getAuthBootstrapStorageKey(window.location.host);
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const isTerminalAuthBootstrapFailure = (error: unknown): boolean => {
  const status = getUserFacingErrorStatus(error);
  return status === 401 || status === 403;
};

const fallbackOnTransientAuthFailure = <T,>(error: unknown, fallback: T): T => {
  if (isTerminalAuthBootstrapFailure(error)) throw error;
  return fallback;
};

type SettledAuthRequest<T> =
  | { ok: true; value: T }
  | { ok: false; error: unknown };

const settleAuthRequest = async <T,>(request: Promise<T>): Promise<SettledAuthRequest<T>> => {
  try {
    return { ok: true, value: await request };
  } catch (error) {
    return { ok: false, error };
  }
};

const isSameIdentity = (left: User | null | undefined, right: User | null | undefined): boolean =>
  Boolean(left && right && Number(left.id) === Number(right.id));

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [accessPolicy, setAccessPolicy] = useState<FrontendAccessPolicy | null>(null);
  const [entryContext, setEntryContext] = useState<PlatformEntryContext | null>(null);
  const [loading, setLoading] = useState(true);
  const authRequestGuardRef = useRef(new AuthRequestGuard());
  const DEV_BYPASS_AUTH = getDevBypassAuth();

  const isCurrentAuthRequest = (lease: AuthRequestLease, expectedToken?: string): boolean =>
    expectedToken === undefined
      ? authRequestGuardRef.current.isCurrent(lease)
      : authRequestGuardRef.current.isCurrentForToken(
        lease,
        expectedToken,
        localStorage.getItem('access_token'),
      );

  useEffect(() => {
    const authRequestGuard = authRequestGuardRef.current;
    const pendingWorkspaceHandoff = typeof window !== 'undefined'
      ? readWorkspaceHandoffUrl(window.location.href).handoffCode
      : null;
    if (pendingWorkspaceHandoff) {
      // LoginPage owns the one-time exchange. Starting profile/policy
      // bootstrap with an old origin-local token here creates a race where
      // its delayed 401 can erase the newly exchanged workspace token.
      authRequestGuard.invalidate();
      setUser(null);
      setAccessPolicy(null);
      setEntryContext(null);
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('access_token');
    if (token) {
      if (DEV_BYPASS_AUTH) {
        // DEV: восстанавливаем фейкового пользователя
        const fakeUser = buildDevBypassUser();
        setUser(fakeUser);
        const policy = buildFallbackFrontendAccessPolicy(fakeUser.role);
        setAccessPolicy(policy);
        setEntryContext(buildDevBypassEntryContext(fakeUser, policy));
        setLoading(false);
        return;
      }
      if (!scheduleClientAuthExpiry(token)) {
        authRequestGuard.invalidate();
        clearStoredAuthBootstrap();
        setUser(null);
        setAccessPolicy(null);
        setEntryContext(null);
        setLoading(false);
        return;
      }
      const authLease = authRequestGuard.begin();
      const cachedSnapshot = readStoredAuthBootstrap(token);
      const jwtBootstrapUser = cachedSnapshot ? null : buildJwtBootstrapFallbackUser(token);
      const bootstrapUser = cachedSnapshot?.user ?? jwtBootstrapUser;
      const bootstrapAccessPolicy =
        cachedSnapshot?.accessPolicy ??
        (bootstrapUser ? buildFailClosedFrontendAccessPolicy(bootstrapUser) : null);
      if (cachedSnapshot) {
        // A cached identity may make routing feel immediate, but persisted
        // business data is never trusted across a fresh authorization check.
        clearProtectedDataCaches('auth_cached_envelope_revalidation');
        setUser(cachedSnapshot.user);
        setAccessPolicy(cachedSnapshot.accessPolicy ?? buildFailClosedFrontendAccessPolicy(cachedSnapshot.user));
        setEntryContext(resolveTransientPlatformEntryContext(
          cachedSnapshot.user,
          cachedSnapshot.accessPolicy,
          cachedSnapshot.entryContext,
        ));
        setLoading(false);
      } else if (bootstrapUser && bootstrapAccessPolicy) {
        setUser(bootstrapUser);
        setAccessPolicy(bootstrapAccessPolicy);
        // A decoded JWT is only an identity hint. Its fail-closed policy has no
        // working actions, so publishing the router before the fresh profile,
        // policy and entry context arrive can bounce an active explicit user
        // between /tickets and /access-pending.
      }

      const timeoutId = setTimeout(() => {
        if (!isCurrentAuthRequest(authLease, token)) return;
        if (bootstrapUser) {
          setEntryContext(resolveTransientPlatformEntryContext(
            bootstrapUser,
            bootstrapAccessPolicy,
            cachedSnapshot?.entryContext,
          ));
          setLoading(false);
          return;
        }
        setLoading(false);
        setUser(null);
        setAccessPolicy(null);
        setEntryContext(null);
      }, AUTH_CHECK_TIMEOUT_MS);
      let cancelled = false;
      const refreshAuth = async () => {
        const profilePromise = authService.getCurrentUser(authLease.signal);
        // Settle the parallel policy request immediately. If it rejects before
        // the profile request completes, a bare rejected promise would otherwise
        // briefly become an unhandled rejection in the browser.
        const accessPolicyPromise = settleAuthRequest(authService.getAccessPolicy(authLease.signal));
        try {
          const currentUser = await profilePromise;
          if (cancelled || !isCurrentAuthRequest(authLease, token)) return;
          const fallbackPolicy = buildFailClosedFrontendAccessPolicy(currentUser);
          if (bootstrapUser && !isSameIdentity(bootstrapUser, currentUser)) {
            clearProtectedDataCaches('auth_identity_revalidated');
            clearStoredAuthBootstrap();
            clearActiveContour();
          }
          const accessPolicyResult = await accessPolicyPromise;
          if (cancelled || !isCurrentAuthRequest(authLease, token)) return;
          const resolvedAccessPolicy = accessPolicyResult.ok
            ? accessPolicyResult.value
            : fallbackOnTransientAuthFailure(accessPolicyResult.error, null);
          const nextAccessPolicy = resolvedAccessPolicy ?? fallbackPolicy;
          const nextEntryContext = await authService.getEntryContext(authLease.signal)
            .catch((error) => fallbackOnTransientAuthFailure(
              error,
              resolveTransientPlatformEntryContext(
                currentUser,
                nextAccessPolicy,
                cachedSnapshot?.entryContext,
              ),
            ));
          if (cancelled || !isCurrentAuthRequest(authLease, token)) return;
          const previousEnvelopeFingerprint = bootstrapUser
            ? buildAccessEnvelopeFingerprint(
              bootstrapUser,
              bootstrapAccessPolicy,
              cachedSnapshot?.entryContext,
            )
            : null;
          const nextEnvelopeFingerprint = buildAccessEnvelopeFingerprint(
            currentUser,
            nextAccessPolicy,
            nextEntryContext,
          );
          if (
            previousEnvelopeFingerprint
            && previousEnvelopeFingerprint !== nextEnvelopeFingerprint
          ) {
            // Purge and remount before publishing the refreshed envelope. This
            // also covers grant changes inside the same selected contour.
            clearProtectedDataCaches('auth_access_envelope_changed');
          }
          setUser(currentUser);
          setAccessPolicy(nextAccessPolicy);
          setEntryContext(nextEntryContext);
          writeStoredAuthBootstrap(currentUser, nextAccessPolicy, nextEntryContext);
        } catch (error) {
          if (cancelled || !isCurrentAuthRequest(authLease, token)) return;
          if (isTerminalAuthBootstrapFailure(error)) {
            authRequestGuardRef.current.invalidate();
            localStorage.removeItem('access_token');
            clearProtectedDataCaches('auth_bootstrap_rejected');
            clearStoredAuthBootstrap();
            setUser(null);
            setAccessPolicy(null);
            setEntryContext(null);
          } else if (bootstrapUser && bootstrapAccessPolicy) {
            setUser(bootstrapUser);
            setAccessPolicy(bootstrapAccessPolicy);
            setEntryContext(resolveTransientPlatformEntryContext(
              bootstrapUser,
              bootstrapAccessPolicy,
              cachedSnapshot?.entryContext,
            ));
          }
          setLoading(false);
        } finally {
          clearTimeout(timeoutId);
          if (isCurrentAuthRequest(authLease, token)) {
            setLoading(false);
          }
        }
      };
      void refreshAuth();
      return () => {
        cancelled = true;
        authRequestGuard.invalidate();
        clearTimeout(timeoutId);
      };
    } else {
      if (getDevBypassAutoLogin()) {
        const fakeUser = buildDevBypassUser();
        localStorage.setItem('access_token', 'dev-fake-token');
        setUser(fakeUser);
        const policy = buildFallbackFrontendAccessPolicy(fakeUser.role);
        setAccessPolicy(policy);
        setEntryContext(buildDevBypassEntryContext(fakeUser, policy));
        setLoading(false);
        return;
      }
      setLoading(false);
    }
  }, [DEV_BYPASS_AUTH]);

  useEffect(() => {
    const handleLogout = () => {
      authRequestGuardRef.current.invalidate();
      clearProtectedDataCaches('auth_logout_event');
      setUser(null);
      setAccessPolicy(null);
      setEntryContext(null);
      setLoading(false);
      clearStoredAuthBootstrap();
      clearActiveContour();
      clearActiveWorkspace();
    };
    window.addEventListener('auth:logout', handleLogout as EventListener);
    return () => {
      window.removeEventListener('auth:logout', handleLogout as EventListener);
    };
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const authLease = authRequestGuardRef.current.begin();
    clearProtectedDataCaches('login_identity_transition');
    clearStoredAuthBootstrap();
    clearActiveContour();
    clearActiveWorkspace();
    localStorage.removeItem('access_token');
    setUser(null);
    setAccessPolicy(null);
    setEntryContext(null);
    if (DEV_BYPASS_AUTH) {
      const fakeUser = buildDevBypassUser(credentials.username);
      localStorage.setItem('access_token', 'dev-fake-token');
      if (!authRequestGuardRef.current.isCurrent(authLease)) return;
      setUser(fakeUser);
      const policy = buildFallbackFrontendAccessPolicy(fakeUser.role);
      setAccessPolicy(policy);
      setEntryContext(buildDevBypassEntryContext(fakeUser, policy));
      return;
    }

    const response = await authService.login(credentials, authLease.signal);
    if (!authRequestGuardRef.current.isCurrent(authLease)) return;
    const token = response.token || response.access_token;
    if (!token) {
      throw new Error('Token not received from API');
    }
    localStorage.setItem('access_token', token);
    scheduleClientAuthExpiry(token);
    const currentUser = response.user ?? (await authService.getCurrentUser(authLease.signal));
    if (!isCurrentAuthRequest(authLease, token)) return;
    const fallbackPolicy = buildFailClosedFrontendAccessPolicy(currentUser);
    try {
      const policy = await authService.getAccessPolicy(authLease.signal)
        .catch((error) => fallbackOnTransientAuthFailure(error, fallbackPolicy));
      if (!isCurrentAuthRequest(authLease, token)) return;
      const nextEntryContext = await authService.getEntryContext(authLease.signal)
        .catch((error) => fallbackOnTransientAuthFailure(
          error,
          resolveTransientPlatformEntryContext(currentUser, policy, null),
        ));
      if (!isCurrentAuthRequest(authLease, token)) return;
      setUser(currentUser);
      setAccessPolicy(policy);
      setEntryContext(nextEntryContext);
      writeStoredAuthBootstrap(currentUser, policy, nextEntryContext);
    } catch (error) {
      if (isCurrentAuthRequest(authLease, token) && isTerminalAuthBootstrapFailure(error)) {
        authRequestGuardRef.current.invalidate();
        localStorage.removeItem('access_token');
        clearProtectedDataCaches('login_identity_rejected');
        clearStoredAuthBootstrap();
        clearActiveContour();
        clearActiveWorkspace();
        setUser(null);
        setAccessPolicy(null);
        setEntryContext(null);
      }
      throw error;
    }
  };

  const register = async (payload: RegistrationPayload) => {
    const authLease = authRequestGuardRef.current.begin();
    clearProtectedDataCaches('registration_identity_transition');
    clearStoredAuthBootstrap();
    clearActiveContour();
    clearActiveWorkspace();
    localStorage.removeItem('access_token');
    setUser(null);
    setAccessPolicy(null);
    setEntryContext(null);
    const response = await authService.register(payload, authLease.signal);
    if (!authRequestGuardRef.current.isCurrent(authLease)) return;
    const token = response.token || response.access_token;
    if (!token) {
      throw new Error('Token not received from API');
    }
    localStorage.setItem('access_token', token);
    scheduleClientAuthExpiry(token);
    const currentUser = response.user ?? (await authService.getCurrentUser(authLease.signal));
    if (!isCurrentAuthRequest(authLease, token)) return;
    const fallbackPolicy = buildFailClosedFrontendAccessPolicy(currentUser);
    try {
      const policy = await authService.getAccessPolicy(authLease.signal)
        .catch((error) => fallbackOnTransientAuthFailure(error, fallbackPolicy));
      if (!isCurrentAuthRequest(authLease, token)) return;
      const nextEntryContext = await authService.getEntryContext(authLease.signal)
        .catch((error) => fallbackOnTransientAuthFailure(
          error,
          resolveTransientPlatformEntryContext(currentUser, policy, null),
        ));
      if (!isCurrentAuthRequest(authLease, token)) return;
      setUser(currentUser);
      setAccessPolicy(policy);
      setEntryContext(nextEntryContext);
      writeStoredAuthBootstrap(currentUser, policy, nextEntryContext);
    } catch (error) {
      if (isCurrentAuthRequest(authLease, token) && isTerminalAuthBootstrapFailure(error)) {
        authRequestGuardRef.current.invalidate();
        localStorage.removeItem('access_token');
        clearProtectedDataCaches('registration_identity_rejected');
        clearStoredAuthBootstrap();
        clearActiveContour();
        clearActiveWorkspace();
        setUser(null);
        setAccessPolicy(null);
        setEntryContext(null);
      }
      throw error;
    }
  };

  const refreshEntryContext = async (): Promise<PlatformEntryContext | null> => {
    if (!user) return null;
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    const authLease = authRequestGuardRef.current.begin();
    try {
      const next = await authService.getEntryContext(authLease.signal)
        .catch((error) => fallbackOnTransientAuthFailure(
          error,
          resolveTransientPlatformEntryContext(user, accessPolicy, entryContext),
        ));
      if (!isCurrentAuthRequest(authLease, token)) return null;
      if (
        buildAccessEnvelopeFingerprint(user, accessPolicy, entryContext)
        !== buildAccessEnvelopeFingerprint(user, accessPolicy, next)
      ) {
        clearProtectedDataCaches('entry_context_envelope_changed');
      }
      setEntryContext(next);
      writeStoredAuthBootstrap(user, accessPolicy, next);
      return next;
    } catch (error) {
      if (isCurrentAuthRequest(authLease, token) && isTerminalAuthBootstrapFailure(error)) {
        forceClientAuthLogout('entry_context_rejected', { expectedToken: token, redirect: true });
      }
      throw error;
    }
  };

  const logout = () => {
    authRequestGuardRef.current.invalidate();
    clearActiveContour();
    clearActiveWorkspace();
    clearWorkspaceResume();
    authService.logout();
    setUser(null);
    setAccessPolicy(null);
    setEntryContext(null);
    clearStoredAuthBootstrap();
  };

  const providedAccessPolicy = DEV_BYPASS_AUTH && user && !accessPolicy
    ? buildFallbackFrontendAccessPolicy(user.role)
    : accessPolicy;
  const providedEntryContext = DEV_BYPASS_AUTH && user && providedAccessPolicy && (!entryContext || entryContext.entry_kind === 'pending')
    ? buildDevBypassEntryContext(user, providedAccessPolicy)
    : entryContext;

  return (
    <AuthContext.Provider
      value={{
        user,
        accessPolicy: providedAccessPolicy,
        entryContext: providedEntryContext,
        loading,
        login,
        register,
        logout,
        refreshEntryContext,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
